import React from 'react';
import {
  CommentConversion,
  Content,
  ContentNode,
  ContentNodeType,
  ContentType,
} from '@amzn/awscat-pattern-library-service-model';
import { getHTML, getJSON } from '@amzn/awscat-editor';
import {
  convertCodeRepository,
  convertContentMetadata,
  convertContentTypeMetadata,
  convertTemplateMetadata,
  getContentPages,
  getH2Heading,
  getH3Heading,
  getSeparator,
  getTemplateSchema,
  getTemplateSections,
} from '../../utils/ConverterUtils';
import { ContentSchemaSection } from '../../../model/ContentSchemaSection';
import JSONSerializer from '../../../common/JSONSerializer';
import { localLog } from '../../../loggers/Logger';
import ContentConverterInput from './ContentConverterInput';
import { ContentConversionSubTypeMap } from '../../utils/SelectableOptions';
import { autoGenerateHref, concatContentData } from '../../../api/ContentUtil';
import { ROOT_SECTION_ID } from '../../../model/Contents';

interface Props {
  content: Content;
  onDismiss?: () => void;
  onError: (errorMsg?: string) => void;
  onSuccess: (updatedContent: Content, commentConversions: CommentConversion[]) => void;
  toContentType: ContentType;
}

const SPECIAL_SECTIONS = ['epics', 'troubleshooting'];

const MultiToSinglePageConverter: React.FunctionComponent<Props> = (props: Props) => {
  const {
    content,
    content: {
      contentMetadata: { contentType },
    },
    onDismiss,
    onError,
    onSuccess,
    toContentType,
  } = props;
  const contentSubType = ContentConversionSubTypeMap.get(toContentType);
  const commentConversions: CommentConversion[] = [];

  const getContentData = (id: string) => {
    switch (id) {
      case 'epics':
        return [];
      case 'troubleshooting':
        return [];
      default:
        return '';
    }
  };

  const getContentSections = (sectionSchema: any) => {
    const sectionProps = sectionSchema.properties;
    const sections = Object.keys(sectionProps).map((id) => {
      const { label } = sectionProps[id];
      const contentData = getContentData(id);
      return Object.assign(new ContentNode(), {
        id,
        title: label,
        nodeType: ContentNodeType.Section,
        contentData,
      });
    });
    return sections;
  };

  const copySection = (nestedSection: ContentNode, section: ContentNode) => {
    const concatData = concatContentData(
      section.contentData,
      getH3Heading(nestedSection.title),
      nestedSection.contentData,
    );
    section.contentData = getJSON(concatData);
    commentConversions.push(CommentConversion.create(nestedSection.id, section.id));
  };

  const copyPage = (page: ContentNode, section: ContentNode) => {
    let contentData = getHTML(section.contentData) ?? '';
    if (contentData) {
      contentData += getSeparator();
    }
    const concatData = concatContentData(contentData, getH2Heading(page.title), page.contentData);
    section.contentData = getJSON(concatData);
    commentConversions.push(CommentConversion.create(page.id, section.id));
    page.contentSections.forEach((nestedSection) => {
      copySection(nestedSection, section);
    });
    page.subContent.forEach((nestedPage) => {
      copyPage(nestedPage, section);
    });
  };

  const convertMainContentData = (
    contentData: ContentNode,
    newContentSections: ContentNode[],
    pageSectionMap: Map<string, string>,
  ) => {
    const mainSection = newContentSections.find(
      (section) => section.id === pageSectionMap.get(ROOT_SECTION_ID),
    );
    if (mainSection) {
      const concatData = concatContentData(
        mainSection?.contentData ?? '',
        getH2Heading(contentData.title),
        contentData.contentData,
      );
      mainSection.contentData = getJSON(concatData);
      contentData.contentData = '';
      commentConversions.push(CommentConversion.create(ROOT_SECTION_ID, mainSection.id));
      contentData.contentSections.forEach((nestedSection) => {
        copySection(nestedSection, mainSection);
      });
    }
  };

  const convertNestedContentData = (
    contentData: ContentNode,
    newContentSections: ContentNode[],
    pageSectionMap: Map<string, string>,
  ) => {
    const { subContent } = contentData;
    subContent.forEach((page) => {
      const mappedSection = newContentSections.find(
        (section) => section.id === pageSectionMap.get(page.id),
      );
      // @ts-expect-error TS2345 - strict migration
      copyPage(page, mappedSection);
    });
    contentData.subContent = [];
    contentData.contentSections = newContentSections;
  };

  const convertContentData = (contentData: ContentNode, pageSectionMap: Map<string, string>) => {
    // @ts-expect-error TS2345 - strict migration
    const sectionSchema = getTemplateSchema(contentSubType, ContentSchemaSection.ContentSections);
    const newContentSections = getContentSections(sectionSchema);
    convertMainContentData(contentData, newContentSections, pageSectionMap);
    convertNestedContentData(contentData, newContentSections, pageSectionMap);
  };

  const convertContent = (updatedContent: Content, pageSectionMap: Map<string, string>) => {
    const { contentData, contentTypeMetadata, contentMetadata } = updatedContent;
    // @ts-expect-error TS2345 - strict migration
    convertContentTypeMetadata(contentTypeMetadata, contentSubType);
    convertContentMetadata(contentMetadata, toContentType);
    // @ts-expect-error TS2345 - strict migration
    convertTemplateMetadata(updatedContent, contentSubType);
    convertContentData(contentData, pageSectionMap);
    convertCodeRepository(updatedContent, toContentType);
    autoGenerateHref(contentData, contentMetadata, true);
  };

  const convertMultiToSinglePage = (pageSectionMap: Map<string, string>) => {
    const serializer: JSONSerializer = new JSONSerializer();
    const updatedContent = serializer.deserialize<Content>(serializer.stringify(content), Content);
    convertContent(updatedContent, pageSectionMap);
    onSuccess(updatedContent, commentConversions);
  };

  const onConfirm = (pageSectionMap: Map<string, string>) => {
    try {
      convertMultiToSinglePage(pageSectionMap);
    } catch (err) {
      localLog.error(err);
      // @ts-expect-error TS18046 - strict migration
      onError(err.message);
    }
  };

  const getTemplateToSections = () => {
    // @ts-expect-error TS2345 - strict migration
    const templSections = getTemplateSections(contentSubType);
    return templSections.filter((section) => !SPECIAL_SECTIONS.includes(section.id));
  };

  const contentPages = getContentPages(content);
  const templateSections = getTemplateToSections();
  return (
    <ContentConverterInput
      fromSections={contentPages}
      fromContentType={contentType}
      onConfirm={onConfirm}
      onCancel={onDismiss}
      toContentType={toContentType}
      toSections={templateSections}
    />
  );
};

export default MultiToSinglePageConverter;
